import { useEffect, useMemo, useState } from "react";
import { FileText, Printer, Package, PackageOpen, Ship, Plane, Users, DollarSign, TrendingUp, Loader2 } from "lucide-react";
import api, { formatApiError } from "@/lib/api";

function fmt(n, cur = "XOF") {
  return `${Math.round(Number(n || 0)).toLocaleString("fr-FR")} ${cur}`;
}
function ymNow() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}
function formatDate(iso) {
  try { return new Date(iso).toLocaleDateString("fr-FR"); } catch { return iso; }
}
const MONTH_LABEL = ["", "Janvier","Février","Mars","Avril","Mai","Juin","Juillet","Août","Septembre","Octobre","Novembre","Décembre"];
function readableMonth(ym) {
  const [y, m] = ym.split("-").map(Number);
  return `${MONTH_LABEL[m] || ym} ${y}`;
}

export default function ReportsPanel() {
  const [month, setMonth] = useState(ymNow());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  useEffect(() => {
    setLoading(true);
    api.get(`/admin/reports/monthly?month=${month}`)
      .then((r) => setData(r.data))
      .catch((e) => setErr(formatApiError(e)))
      .finally(() => setLoading(false));
  }, [month]);

  const printReport = () => window.print();

  const months = useMemo(() => {
    const arr = [];
    const d = new Date();
    for (let i = 0; i < 12; i++) {
      const y = d.getFullYear();
      const m = d.getMonth() + 1;
      arr.push(`${y}-${String(m).padStart(2, "0")}`);
      d.setMonth(d.getMonth() - 1);
    }
    return arr;
  }, []);

  if (err) return <p className="text-sm text-red-500">{err}</p>;
  if (loading || !data) return <div className="flex items-center gap-2 text-sm text-[var(--ink-soft)]"><Loader2 className="animate-spin" size={16}/> Chargement du rapport…</div>;

  return (
    <div className="space-y-6" data-testid="admin-reports">
      <div className="flex items-center justify-between gap-3 flex-wrap print:hidden">
        <div>
          <p className="kicker !text-[var(--secondary)] flex items-center gap-1"><FileText size={12}/> Rapport mensuel</p>
          <h3 className="font-display text-2xl font-black mt-1">Négosino — {readableMonth(month)}</h3>
        </div>
        <div className="flex items-center gap-2">
          <select value={month} onChange={(e) => setMonth(e.target.value)} className="input-field !py-2" data-testid="report-month-select">
            {months.map((m) => <option key={m} value={m}>{readableMonth(m)}</option>)}
          </select>
          <button onClick={printReport} className="btn-primary !py-2 !px-3 text-xs" data-testid="report-print">
            <Printer size={14}/> Imprimer / PDF
          </button>
        </div>
      </div>

      {/* Print header */}
      <div className="hidden print:block mb-4">
        <h1 className="text-2xl font-black">Négosino — Rapport {readableMonth(month)}</h1>
        <p className="text-xs text-[var(--ink-soft)]">Généré le {new Date().toLocaleString("fr-FR")}</p>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Stat icon={PackageOpen} label="Groupages du mois" value={data.groupages.length} />
        <Stat icon={Users} label="Clients impliqués" value={data.totals.clients_involved} />
        <Stat icon={DollarSign} label="Achats du mois" value={fmt(data.totals.purchases)} small />
        <Stat icon={Ship} label="Conteneurs en route" value={data.totals.containers_in_transit} />
      </div>

      {/* Achats du mois (groupages détaillés) */}
      <div>
        <h4 className="font-display text-xl font-bold flex items-center gap-2 mb-3"><PackageOpen size={18}/> Achats du mois (groupages)</h4>
        {data.groupages.length === 0 ? (
          <p className="text-sm text-[var(--ink-soft)] italic">Aucun groupage créé sur ce mois.</p>
        ) : (
          <div className="space-y-3">
            {data.groupages.map((g) => (
              <div key={g.id} className="card-flat p-4">
                <div className="flex items-center justify-between flex-wrap gap-2">
                  <div>
                    <p className="font-mono text-xs text-[var(--ink-soft)]">{g.reference} · {formatDate(g.created_at)}</p>
                    <p className="font-bold">{g.name}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-xs text-[var(--ink-soft)]">Reçu / Dépensé / Solde</p>
                    <p className="text-sm">
                      <span className="text-green-600 font-bold">{fmt(g.total_received, g.currency)}</span> /
                      <span className="text-orange-600 font-bold"> {fmt(g.total_spent, g.currency)}</span> /
                      <span className={g.balance >= 0 ? "text-blue-600 font-bold" : "text-red-600 font-bold"}> {fmt(g.balance, g.currency)}</span>
                    </p>
                  </div>
                </div>
                {(g.contributions || []).length > 0 && (
                  <table className="w-full mt-3 text-sm">
                    <thead>
                      <tr className="text-xs uppercase tracking-widest text-[var(--ink-soft)] text-left">
                        <th className="pb-2">Client</th>
                        <th className="pb-2 text-right">Reçu</th>
                        <th className="pb-2 text-right">Dépensé</th>
                        <th className="pb-2">Produits</th>
                      </tr>
                    </thead>
                    <tbody>
                      {g.contributions.map((c) => {
                        const spent = (c.products || []).reduce((a, p) => a + (Number(p.unit_price || 0) * Number(p.qty || 0)), 0);
                        return (
                          <tr key={c.id} className="border-t border-black/5">
                            <td className="py-1.5 font-bold">{c.client_name}</td>
                            <td className="py-1.5 text-right text-green-600">{fmt(c.amount_received, c.currency)}</td>
                            <td className="py-1.5 text-right text-orange-600">{fmt(spent, c.currency)}</td>
                            <td className="py-1.5 text-xs text-[var(--ink-soft)]">
                              {(c.products || []).map((p) => `${p.name} (x${p.qty || 1})`).join(", ") || "—"}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Conteneurs en route */}
      <div>
        <h4 className="font-display text-xl font-bold flex items-center gap-2 mb-3"><Ship size={18}/> Conteneurs en cours de route</h4>
        {data.containers.length === 0 ? (
          <p className="text-sm text-[var(--ink-soft)] italic">Aucun conteneur maritime en transit actuellement.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-xs uppercase tracking-widest text-[var(--ink-soft)] text-left border-b border-black/10">
                  <th className="py-2">N° suivi</th>
                  <th className="py-2">Client</th>
                  <th className="py-2">Contenu</th>
                  <th className="py-2">Trajet</th>
                  <th className="py-2">Statut</th>
                  <th className="py-2 text-right">Volume</th>
                  <th className="py-2 text-right">Montant</th>
                </tr>
              </thead>
              <tbody>
                {data.containers.map((c) => (
                  <tr key={c.id} className="border-b border-black/5" data-testid={`container-row-${c.tracking_number}`}>
                    <td className="py-2 font-mono">{c.tracking_number}</td>
                    <td className="py-2 font-bold">{c.client_name}</td>
                    <td className="py-2 text-[var(--ink-soft)]">{(c.description || "").slice(0, 40)}</td>
                    <td className="py-2 text-xs">{c.origin} → {c.destination}</td>
                    <td className="py-2"><span className="text-[10px] font-bold uppercase bg-[var(--accent)]/10 text-[var(--accent)] px-2 py-0.5 rounded-full">{(c.current_status || "").replace(/_/g, " ")}</span></td>
                    <td className="py-2 text-right">{c.volume_cbm ? `${c.volume_cbm} m³` : "—"}</td>
                    <td className="py-2 text-right font-bold">{c.amount_due ? fmt(c.amount_due, c.amount_currency || "XOF") : "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Toutes expéditions du mois */}
      <div>
        <h4 className="font-display text-xl font-bold flex items-center gap-2 mb-3"><Plane size={18}/> Toutes les expéditions de {readableMonth(month)}</h4>
        <p className="text-xs text-[var(--ink-soft)] mb-3">
          {data.totals.shipments_month} expéditions · {data.totals.shipments_air} aériennes · {data.totals.shipments_maritime} maritimes
        </p>
        {data.shipments.length === 0 ? (
          <p className="text-sm text-[var(--ink-soft)] italic">Aucune expédition ce mois-ci.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-xs uppercase tracking-widest text-[var(--ink-soft)] text-left border-b border-black/10">
                  <th className="py-2">Date</th>
                  <th className="py-2">N° suivi</th>
                  <th className="py-2">Client</th>
                  <th className="py-2">Service</th>
                  <th className="py-2">Statut</th>
                  <th className="py-2 text-right">Montant</th>
                </tr>
              </thead>
              <tbody>
                {data.shipments.map((s) => (
                  <tr key={s.id} className="border-b border-black/5">
                    <td className="py-2 text-xs">{formatDate(s.created_at)}</td>
                    <td className="py-2 font-mono">{s.tracking_number}</td>
                    <td className="py-2 font-bold">{s.client_name}</td>
                    <td className="py-2 text-xs">{s.service_type}</td>
                    <td className="py-2 text-xs">{(s.current_status || "").replace(/_/g, " ")}</td>
                    <td className="py-2 text-right font-bold">{s.amount_due ? fmt(s.amount_due, s.amount_currency || "XOF") : "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

function Stat({ icon: Icon, label, value, small }) {
  return (
    <div className="card-flat p-5">
      <Icon className="text-[var(--accent)]" size={18} />
      <p className="text-xs uppercase tracking-widest text-[var(--ink-soft)] mt-3">{label}</p>
      <p className={`font-display ${small ? "text-lg" : "text-2xl"} font-black mt-1`}>{value}</p>
    </div>
  );
}
